import { Divider, Table, TablePaginationConfig, Tag } from 'antd'
import React from 'react'

import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import usePage from 'src/hooks/usePage'
import { searchTreasureHunterReport } from 'src/api/reports'

import { useCategoryMap } from 'src/hooks/GamesCategoryContext'
import TreasureHunterReportSearch from './treasureHunterReportSearch'
import { SorterResult, TableCurrentDataSource } from 'antd/lib/table/interface'

const { Column } = Table

const TreasureHunterReportPage: React.FC = () => {
  const { pageData, onQuery, appendSearchForm, submitSearchForm, fetchList } =
    usePage({
      getData: searchTreasureHunterReport,
      searchData: searchTreasureHunterReport,
      searchType: 'POST',
    })
  const { categoryMap, filterGamesByType, findCategoriesForGame } =
    useCategoryMap()

  pageData.data.forEach((gameData) => {
    const categories = findCategoriesForGame(gameData.gameId)
    gameData.categories = categories
  })
  type SortItem = { field: string; order: 'asc' | 'desc' }

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, (string | number | boolean)[] | null>,
    sorter: SorterResult<any> | SorterResult<any>[],
    extra: TableCurrentDataSource<any>
  ) => {
    const current = pagination.current ?? 1
    const pageSize =
      pagination.pageSize ?? (parseInt(pageData.query.pageSize) || 10)

    const sorterArr = Array.isArray(sorter) ? sorter : [sorter]
    const sorts: SortItem[] = sorterArr
      .filter((s) => s && s.field && s.order)
      .map((s) => ({
        field: String(s.field),
        order: s.order === 'ascend' ? 'asc' : 'desc',
      }))

    if (extra.action === 'paginate') {
      appendSearchForm({ buttonAction: '' })
      onQuery({
        page: String(current - 1),
        pageSize: String(pageSize),
      })
      return
    }

    if (extra.action === 'sort') {
      onQuery({
        page: String(current - 1),
        pageSize: String(pageSize),
        sorts: JSON.stringify(sorts),
      })
      return
    }

    if (extra.action === 'filter') {
      onQuery({
        page: String(current - 1),
        pageSize: String(pageSize),
        sorts: JSON.stringify(sorts),
      })
    }
  }

  return (
    <>
      <Divider />
      <DndProvider backend={HTML5Backend}>
        <Table
          className="table-sorting"
          rowKey="eventId"
          dataSource={pageData.data}
          loading={pageData.loading}
          onChange={handleTableChange}
          pagination={{
            current: parseInt(pageData.query.page) + 1,
            total: pageData.total,
            pageSize: parseInt(pageData.query.pageSize) || 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
          }}
          title={() => (
            <TreasureHunterReportSearch
              appendSearchForm={appendSearchForm}
              submitSearchForm={submitSearchForm}
            />
          )}
        >
          <Column
            width={120}
            dataIndex="gameId"
            title="Game Name"
            sorter={{ multiple: 1 }}
          />
          <Column
            dataIndex="userName"
            title="User Name"
            sorter={{ multiple: 1 }}
          />
          <Column
            dataIndex="gender"
            sorter={{ multiple: 1 }}
            title="Gender"
            filters={[
              {
                text: 'Male',
                value: 'MALE',
              },
              {
                text: 'Female',
                value: 'FEMALE',
              },
            ]}
            filterMode="tree"
            onFilter={(value: string, record: any) => record.gender === value}
          />
          <Column dataIndex="age" title="Age" sorter={{ multiple: 1 }} />
          <Column
            dataIndex="providerCountry"
            title="Country"
            sorter={{ multiple: 1 }}
          />
          <Column
            dataIndex="accessLevel"
            title="User Type"
            sorter={{ multiple: 1 }}
            render={(text) => {
              const colorStyle: React.CSSProperties = {
                color: 'white',
                textAlign: 'center',
                width: 100,
                display: 'inline-block',
                borderRadius: '4px',
                padding: '0 8px',
              }

              switch (text) {
                case 'FREEMIUM':
                  colorStyle.backgroundColor = 'rgba(158, 54, 187, 0.78)'
                  colorStyle.color = 'rgba(222, 222, 222, 1)'
                  break
                case 'GUEST':
                  colorStyle.backgroundColor = 'rgba(54, 187, 156, 0.78)'
                  colorStyle.color = 'rgba(222, 222, 222, 1)'
                  break
                case 'SUBSCRIBED':
                  colorStyle.backgroundColor = 'rgba(187, 54, 132, 0.78)'
                  colorStyle.color = 'rgba(222, 222, 222, 1)'
                  break
                default:
                  colorStyle.backgroundColor = 'rgba(217, 217, 217, 0.78)'
                  colorStyle.color = 'rgba(222, 222, 222, 1)'
              }

              const displayText = text === 'SUBSCRIBED' ? 'PREMIUM' : text
              return <Tag style={colorStyle}>{displayText}</Tag>
            }}
          />
          <Column
            dataIndex="deviceType"
            title="Device Type"
            sorter={{ multiple: 1 }}
          />

          <Column
            dataIndex="eventDuration"
            title="Event Duration"
            sorter={{ multiple: 1 }}
            render={(text) => <span>{text} sec</span>}
          />
          <Column
            dataIndex="eventDate"
            title="Event Date"
            sorter={{ multiple: 5 }}
          />
          <Column<{ rating: number; votes: number }>
            title="Rating"
            sorter={{ multiple: 1 }}
            render={(_, record) => {
              if (record.votes > 0) {
                const average = record.rating / record.votes
                return average.toFixed(1)
              }

              return '0.0'
            }}
          />
          <Column<{ votes: number }>
            title="Votes"
            sorter={{ multiple: 1 }}
            render={(_, record) => {
              if (record.votes > 0) {
                return record.votes
              }
              return '0'
            }}
          />
          <Column
            dataIndex="clientName"
            title="Client Name"
            sorter={{ multiple: 1 }}
          />
        </Table>
      </DndProvider>
    </>
  )
}

TreasureHunterReportPage.displayName = 'TreasureHunterReportPage'
export default TreasureHunterReportPage
